import React from 'react';
import { StyleSheet, TextInput, View } from 'react-native';

import { submitFormSafely, useAppForm } from '@components/Form';
import { CountryPickerModal } from '@components/Picker';
import ScreenContainer from '@components/ScreenContainer';
import { TextInputAffix } from '@components/TextInput';
import { Caption, IconTitle, Paragraph, PrimaryHeadline } from '@components/Typography';
import { validateUserBirthdate } from '@db/Users';
import { useCreateUserPrivateMutation } from '@db/Users/Queries';
import { errorAlert } from '@helpers/Alert';
import { stringToDate } from '@helpers/Dates';
import { validatePhoneNumber } from '@helpers/PhoneNumber';
import { OnboardingRoute } from '@navigation/Routes';
import { UserPrivateFormScreenProps } from '@navigation/Types';
import { Country } from '@types';

const DEFAULT_COUNTRY: Country = {
  name: 'United States',
  code: 'US',
  dial_code: '+1',
  flag: '🇺🇸',
};

type UserPrivateFormValues = {
  birthdate: string;
  country: Country;
  phoneNumber: string;
};

export default function UserPrivateFormScreen({ navigation }: UserPrivateFormScreenProps) {
  const [countryPickerVisible, setCountryPickerVisible] = React.useState<boolean>(false);
  const phoneNumberRef = React.useRef<TextInput>(null);
  const createUserPrivateMutation = useCreateUserPrivateMutation();

  const form = useAppForm({
    defaultValues: {
      birthdate: '',
      country: DEFAULT_COUNTRY,
      phoneNumber: '',
    } satisfies UserPrivateFormValues,
    onSubmit: async ({ value }) => {
      try {
        const parsedBirthdate = stringToDate(value.birthdate);
        const { parsedPhoneNumber } = validatePhoneNumber(value.phoneNumber, value.country.code);

        await createUserPrivateMutation.mutateAsyncOrThrow({
          userPrivate: {
            birthdate: parsedBirthdate,
            phoneNumber: parsedPhoneNumber,
          },
        });
        navigation.navigate(OnboardingRoute.Welcome);
      } catch (error) {
        errorAlert(error);
        throw error;
      }
    },
  });

  const renderHeader = (
    icon: React.ComponentProps<typeof IconTitle>['icon'],
    title: string,
    caption: string
  ) => (
    <View>
      <IconTitle icon={icon} text={title} />
      <Caption style={styles.caption}>{caption}</Caption>
    </View>
  );

  const renderBirthdateInput = () => (
    <View style={styles.itemContainer}>
      {renderHeader(
        'calendar-outline',
        'Birthdate',
        "Your birthdate is used to verify you're an adult."
      )}
      <View style={styles.dateContainer}>
        <form.AppField
          name="birthdate"
          validators={{
            onChange: ({ value }) => validateUserBirthdate(stringToDate(value)) || undefined,
            onSubmit: ({ value }) => validateUserBirthdate(stringToDate(value)) || undefined,
          }}>
          {(field) => (
            <field.DateInput
              returnKeyType="next"
              // Note: the value of this text input is handled internally,
              // which seems to cause the keyboard to be dismissed when the
              // return key is pressed. we modify the submit behavior to
              // prevent the keyboard rapidly dismissing and reopening
              submitBehavior="submit"
              transformValue={(text) => text.trim()}
              onSubmitEditing={() => phoneNumberRef.current?.focus()}
              containerStyle={styles.textInputContainer}
              testID="screens.UserPrivateFormScreen.DatePickerInput"
            />
          )}
        </form.AppField>
      </View>
    </View>
  );

  const renderPhoneNumberInput = () => (
    <View style={styles.itemContainer}>
      {renderHeader(
        'phone-outline',
        'Phone number',
        'Your phone number is used to verify ownership of your account.'
      )}
      <View style={styles.row}>
        <View style={styles.phoneNumberInputContainer}>
          <form.AppField
            name="phoneNumber"
            validators={{
              onChange: ({ fieldApi, value }) =>
                validatePhoneNumber(value, fieldApi.form.getFieldValue('country').code).error ||
                undefined,
              onChangeListenTo: ['country'],
              onSubmit: ({ fieldApi, value }) =>
                validatePhoneNumber(value, fieldApi.form.getFieldValue('country').code).error ||
                undefined,
            }}>
            {(field) => (
              <form.Subscribe selector={(state) => state.values.country}>
                {(country) => (
                  <field.TextInput
                    inputRef={phoneNumberRef}
                    placeholder="Enter your phone number"
                    keyboardType="number-pad"
                    returnKeyType="done"
                    left={
                      <TextInputAffix
                        text={country.flag}
                        onPress={() => setCountryPickerVisible(true)}
                      />
                    }
                    transformValue={(text) => text.replace(/[^0-9]/g, '').trim()}
                    containerStyle={styles.textInputContainer}
                    onSubmitEditing={onPress}
                    testID="screens.UserPrivateFormScreen.TextInput.PhoneNumber"
                  />
                )}
              </form.Subscribe>
            )}
          </form.AppField>
        </View>
      </View>
    </View>
  );

  const renderCountryPicker = () => (
    <form.AppField name="country">
      {(field) => (
        <CountryPickerModal
          selectedCountry={field.state.value}
          setSelectedCountry={field.handleChange}
          visible={countryPickerVisible}
          onDismiss={() => setCountryPickerVisible(false)}
        />
      )}
    </form.AppField>
  );

  const onPress = () => submitFormSafely(form);

  return (
    <form.AppForm>
      <ScreenContainer scrollable>
        <View style={styles.screen}>
          <PrimaryHeadline style={styles.header}>Complete your profile</PrimaryHeadline>
          <Paragraph style={styles.paragraph}>
            Joinstar cares about your privacy. All your data is securely encrypted.
          </Paragraph>
          <View>
            {renderBirthdateInput()}
            {renderPhoneNumberInput()}
            {renderCountryPicker()}
          </View>
          <form.SubmitBigButton
            mode="contained"
            style={styles.button}
            testID="screens.UserPrivateFormScreen.Button.Continue">
            Continue
          </form.SubmitBigButton>
        </View>
      </ScreenContainer>
    </form.AppForm>
  );
}

const styles = StyleSheet.create({
  button: {
    marginTop: 20,
  },
  caption: {
    alignSelf: 'flex-start',
  },
  dateContainer: {
    width: '100%',
  },
  header: {
    alignSelf: 'center',
  },
  itemContainer: {
    marginTop: 10,
  },
  paragraph: {
    textAlign: 'center',
  },
  phoneNumberInputContainer: {
    flex: 1,
    width: '100%',
  },
  row: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  screen: {
    paddingHorizontal: 20,
  },
  textInputContainer: {
    marginVertical: 0,
  },
});
